import { showSessionExpiredDialog } from './sessionUtils';
import { RefreshResponse } from '../types/auth';
import { getCachedApiUrl } from './apiConfig';

// Утилита для работы с API через JWT токены и авто-рефреш по 401

const getApiBaseUrl = () => getCachedApiUrl();

const LANGUAGE_STORAGE_KEY = 'lazydoc_app_language';

/** Возвращает значение для заголовка Accept-Language по выбранному в приложении языку */
const getAcceptLanguageHeader = (): string => {
  if (typeof window === 'undefined') return 'ru-RU';
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored === 'en') return 'en';
  return 'ru-RU';
};

// Экспортируем для использования в компонентах
export { getCachedApiUrl };

// Функция для получения токенов из localStorage
export const getTokens = () => {
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  return { accessToken, refreshToken };
};

// Функция для сохранения токенов в localStorage
export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem('access_token', accessToken);
  localStorage.setItem('refresh_token', refreshToken);
};

// Функция для удаления токенов из localStorage
export const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

// Переменная для защиты от race condition при множественных refresh запросах
let refreshPromise: Promise<boolean> | null = null;
let isRefreshing = false;

// Функция для обновления токенов
export const refreshToken = async (): Promise<boolean> => {
  // Если уже идет обновление, возвращаем существующий промис
  if (isRefreshing && refreshPromise) {
    console.log('apiUtils: refresh already in progress, waiting...');
    return refreshPromise;
  }

  // Устанавливаем флаг и создаем промис
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      console.log('apiUtils: refresh...');
      const { refreshToken: refreshTokenValue } = getTokens();

      if (!refreshTokenValue) {
        console.error('apiUtils: no refresh token found');
        return false;
      }

      const response = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
        method: 'POST',
        headers: {
          'X-Refresh-Token': refreshTokenValue,
          'Accept-Language': getAcceptLanguageHeader(),
        }
      });

      if (response.ok) {
        console.log('apiUtils: refresh ok');
        const data: RefreshResponse = await response.json();
        setTokens(data.access_token, data.refresh_token);
        return true;
      } else {
        // Если refresh сам вернул 401, значит refresh токен истек
        if (response.status === 401) {
          console.error('apiUtils: refresh token expired (401)');
        } else {
          console.error('apiUtils: refresh failed:', response.status);
        }
        clearTokens();
        return false;
      }
    } catch (error) {
      console.error('apiUtils: refresh error:', error);
      clearTokens();
      return false;
    } finally {
      // Сбрасываем флаг и промис после завершения
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Функция для выполнения запроса с автоматическим обновлением токена
export const apiRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const { accessToken } = getTokens();

  // Добавляем Authorization заголовок если есть токен
  // Не перезаписываем Content-Type если он уже установлен (например, для FormData)
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Устанавливаем Content-Type только если его нет и это не FormData
  if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  headers['Accept-Language'] = getAcceptLanguageHeader();

  // Выполняем основной запрос
  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Если получили 401 (Unauthorized), пробуем обновить токен и повторить запрос
  if (response.status === 401) {
    // Не обрабатываем 401 для самого refresh запроса, чтобы избежать бесконечного цикла
    if (url.includes('/auth/refresh')) {
      return response;
    }

    console.log('apiUtils: 401 → try refresh');
    const refreshSuccess = await refreshToken();

    if (refreshSuccess) {
      // Получаем новый токен и повторяем запрос
      const { accessToken: newAccessToken } = getTokens();
      const retryHeaders: Record<string, string> = {
        ...headers,
        'Authorization': `Bearer ${newAccessToken}`,
        'Accept-Language': getAcceptLanguageHeader(),
      };

      const retryResponse = await fetch(url, {
        ...options,
        headers: retryHeaders,
      });

      // Если повторный запрос снова вернул 401, значит что-то не так с токеном
      if (retryResponse.status === 401) {
        console.error('apiUtils: retry after refresh still returned 401');
        showSessionExpiredDialog();
        return new Response(JSON.stringify({ error: 'session_expired' }), {
          status: 401,
          statusText: 'Session Expired',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      return retryResponse;
    } else {
      // Если refresh не удался, показываем диалог истекшего сеанса
      console.log('apiUtils: refresh failed, showing session expired dialog');
      showSessionExpiredDialog();
      // Возвращаем специальный статус для истекшего сеанса
      return new Response(JSON.stringify({ error: 'session_expired' }), {
        status: 401,
        statusText: 'Session Expired',
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  return response;
};

// Обертка для GET запросов
export const apiGet = async (endpoint: string): Promise<Response> => {
  const url = endpoint.startsWith('http') ? endpoint : `${getApiBaseUrl()}${endpoint}`;
  return apiRequest(url, {
    method: 'GET',
  });
};

// Обертка для POST запросов
export const apiPost = async (endpoint: string, data?: any): Promise<Response> => {
  const url = endpoint.startsWith('http') ? endpoint : `${getApiBaseUrl()}${endpoint}`;
  const options: RequestInit = {
    method: 'POST',
  };

  if (data) {
    options.headers = {
      'Content-Type': 'application/json',
    };
    options.body = JSON.stringify(data);
  }

  return apiRequest(url, options);
};

// Обертка для PUT запросов
export const apiPut = async (endpoint: string, data?: any): Promise<Response> => {
  const url = endpoint.startsWith('http') ? endpoint : `${getApiBaseUrl()}${endpoint}`;
  const options: RequestInit = {
    method: 'PUT',
  };

  if (data) {
    options.headers = {
      'Content-Type': 'application/json',
    };
    options.body = JSON.stringify(data);
  }

  return apiRequest(url, options);
};

// Обертка для DELETE запросов
export const apiDelete = async (endpoint: string, data?: any): Promise<Response> => {
  const url = endpoint.startsWith('http') ? endpoint : `${getApiBaseUrl()}${endpoint}`;
  const options: RequestInit = {
    method: 'DELETE',
  };

  if (data) {
    options.headers = {
      'Content-Type': 'application/json',
    };
    options.body = JSON.stringify(data);
  }

  return apiRequest(url, options);
};

// Обертка для PATCH запросов
export const apiPatch = async (endpoint: string, data?: any): Promise<Response> => {
  const url = endpoint.startsWith('http') ? endpoint : `${getApiBaseUrl()}${endpoint}`;
  const options: RequestInit = {
    method: 'PATCH',
  };

  if (data) {
    options.headers = {
      'Content-Type': 'application/json',
    };
    options.body = JSON.stringify(data);
  }

  return apiRequest(url, options);
};

// Функция для получения данных текущего пользователя
export const getCurrentUser = async (): Promise<any | null> => {
  try {
    const response = await apiGet('/auth/me');
    if (response.ok) {
      return await response.json();
    } else if (response.status === 401) {
      // Проверяем, является ли это ошибкой истекшего сеанса
      try {
        const errorData = await response.json();
        if (errorData.error === 'session_expired') {
          console.log('Сессия истекла при получении данных пользователя');
          return null;
        }
      } catch {
        // Если не удалось распарсить JSON, считаем обычной ошибкой 401
      }
      console.error('Ошибка получения данных пользователя:', response.status);
      return null;
    } else {
      console.error('Ошибка получения данных пользователя:', response.status);
      return null;
    }
  } catch (error) {
    console.error('Ошибка при получении данных пользователя:', error);
    return null;
  }
};

/** Обновление профиля текущего пользователя (PATCH /auth/me). specialty — профессия. */
export const updateCurrentUserProfile = async (data: { specialty?: string }): Promise<any | null> => {
  try {
    const response = await apiPatch('/auth/me', data);
    if (response.ok) {
      return await response.json();
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || 'Ошибка обновления профиля');
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error('Ошибка обновления профиля');
  }
};

// Функции для аутентификации
export const loginUser = async (credentials: { email: string; password: string }) => {
  const response = await fetch(`${getApiBaseUrl()}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': getAcceptLanguageHeader(),
    },
    body: JSON.stringify(credentials)
  });

  if (!response.ok) {
    throw new Error('Неверные учетные данные');
  }

  return response.json();
};

// Функция для отправки кода подтверждения при регистрации
export const sendRegistrationCode = async (email: string) => {
  const response = await fetch(`${getApiBaseUrl()}/auth/send-registration-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': getAcceptLanguageHeader(),
    },
    body: JSON.stringify({ email })
  });

  if (!response.ok) {
    const errorData = await response.json();
    let errorMessage = 'Ошибка отправки кода';

    if (response.status === 409) {
      errorMessage = 'Пользователь с таким email уже зарегистрирован';
    } else if (response.status === 429) {
      errorMessage = 'Слишком много запросов. Попробуйте через несколько минут';
    } else if (response.status === 500) {
      errorMessage = 'Ошибка отправки email. Попробуйте позже';
    } else {
      errorMessage = errorData.detail || errorData.message || errorMessage;
    }

    throw new Error(errorMessage);
  }

  return response.json();
};

export const registerUser = async (userData: any) => {
  const response = await fetch(`${getApiBaseUrl()}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': getAcceptLanguageHeader(),
    },
    body: JSON.stringify(userData)
  });

  if (!response.ok) {
    const errorData = await response.json();
    let errorMessage = 'Ошибка регистрации';

    if (response.status === 401) {
      errorMessage = 'Неверный код подтверждения или код истек. Запросите новый код';
    } else if (response.status === 409) {
      errorMessage = 'Пользователь с таким email уже существует';
    } else if (response.status === 422) {
      // Обработка ошибок валидации
      if (errorData.detail) {
        if (Array.isArray(errorData.detail)) {
          // Если детали - массив ошибок валидации
          const validationErrors = errorData.detail.map((err: any) =>
            `${err.loc?.join('.')}: ${err.msg}`
          ).join(', ');
          errorMessage = validationErrors;
        } else {
          errorMessage = errorData.detail;
        }
      }
    } else {
      errorMessage = errorData.detail || errorData.message || errorMessage;
    }

    throw new Error(errorMessage);
  }

  return response.json();
};

// Обертка для POST запросов с FormData (загрузка файлов)
export const apiPostFormData = async (endpoint: string, formData: FormData): Promise<Response> => {
  const url = endpoint.startsWith('http') ? endpoint : `${getApiBaseUrl()}${endpoint}`;

  return apiRequest(url, {
    method: 'POST',
    body: formData,
    // Не устанавливаем Content-Type, браузер сам установит multipart/form-data с boundary
  });
};

// Интерфейс для запроса генерации протокола из чата (v2)
export interface GenerateProtocolFromChatRequest {
  chat_id: number;
  specialty_id?: number; // ID специальности из базы (опционально)
  prompt?: string;
  template?: string; // Название шаблона протокола
}

// Интерфейс для ответа генерации протокола из чата
export interface GenerateProtocolFromChatResponse {
  success: boolean;
  message_id: number;
  error: string | null;
}

// Генерация протокола из чата (v2)
export const generateProtocolFromChat = async (request: GenerateProtocolFromChatRequest): Promise<GenerateProtocolFromChatResponse> => {
  const response = await apiPost('/protocols/v2/generate-from-chat', request);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to generate protocol from chat: ${response.status} - ${errorText}`);
  }

  return response.json();
};

// Интерфейс для специальности
export interface Speciality {
  name: string;
  id?: number;
  label?: string;
  /** В ответах v2 может приходить; выдача для выбора — только активные */
  is_active?: boolean;
}

// Поиск специальностей (v2)
// Возвращает специальности текущего пользователя из базы данных
export const searchSpecialities = async (): Promise<Speciality[]> => {
  const response = await apiGet(`/api/v2/searchSpecialities`);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to search specialities: ${response.status} - ${errorText}`);
  }

  const data = await response.json();

  // API возвращает массив объектов {name, id, label}
  if (Array.isArray(data)) {
    return data;
  }

  return [];
};

/** Получение списка профессий/специальностей для выбора при регистрации.
 * GET /specialties/professions возвращает массив строк. */
export const getSpecialtyProfessions = async (): Promise<string[]> => {
  const response = await apiGet('/specialties/professions');

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get professions: ${response.status} - ${errorText}`);
  }

  const data = await response.json();

  if (Array.isArray(data)) {
    return data.filter((item: unknown): item is string => typeof item === 'string');
  }

  return [];
};

// Интерфейс для ответа авторизации BudZdorov
export interface BudzdorovAuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  chat_id: number;
  chat: {
    id: number;
    title: string;
    description: string;
    user_id: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    messages: Array<{
      id: number;
      chat_id: number;
      sender: 'user' | 'ai';
      text: string;
      type: 'text' | 'json' | 'photo' | 'audio';
      time: string;
      created_at: string;
      json_content?: Record<string, any>;
      title?: string;
      specialty?: string;
    }>;
  };
}

// Авторизация через BudZdorov API
export const budzdorovAuth = async (userId: number, protocolId: number): Promise<BudzdorovAuthResponse> => {
  const response = await fetch(`${getApiBaseUrl()}/budZdorov/auth?user_id=${userId}&protocol_id=${protocolId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': getAcceptLanguageHeader(),
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Ошибка авторизации BudZdorov: ${response.status} - ${errorText}`);
  }

  return response.json();
};

// Функция для загрузки/обновления аватара
export const uploadAvatar = async (file: File): Promise<Response> => {
  const formData = new FormData();
  formData.append('file', file);

  return apiPostFormData('/auth/upload-avatar', formData);
};

// Функция для удаления аватара
export const deleteAvatar = async (): Promise<Response> => {
  return apiDelete('/auth/delete-avatar');
}; 